import { useEffect, useRef, useState } from 'react';

import cx from 'classnames';

import { DatePicker, withDateResolver } from 'components/shared/DatePicker';
import { parseDate } from 'components/shared/DatePicker/functions';
import datePickerStyles from 'components/shared/DatePicker/index.module.css';
import Popover from 'components/shared/Popover';
import { handleFilledDates } from 'utils/filledDates';
import { calcNumberOfNightsAdapter, formatDatesRange, formatNumberOfNights } from 'utils/itineraryDetails';
import { SCHEDULE_TYPES } from 'utils/itineraryDetails/constants';

import sharedStyles from '../shared/DatePickerButton/index.module.css';
import SwitchScheduleItemType from '../SwitchScheduleItemType';

const datesRangePlaceholder = 'March 20 - 23';
const singleDatePlaceholder = 'March 20';

const ResolvedDatePicker = withDateResolver(DatePicker);

function DatePickerButton({
  range,
  onChange,
  showNightsCount,
  getExtraRanges,
  noRange,
  scheduleItem,
  readOnly,
  mountEl,
  elementRef,
  setActive,
}) {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const handleOpenPopup = () => {
    setIsPopupOpen(true);
    setActive(true);
  };
  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setActive(false);
  };
  const buttonContainerRef = useRef();
  const isNoData = !range.start && !range.end;
  const numberOfNights = !isNoData && calcNumberOfNightsAdapter(SCHEDULE_TYPES.DATES, scheduleItem.type, range);
  const placeholder = noRange ? singleDatePlaceholder : datesRangePlaceholder;

  useEffect(() => {
    if (elementRef) {
      elementRef.current = {
        open: handleOpenPopup,
      };
    }
  }, []);

  const booked = getExtraRanges().map((range) => ({
    from: parseDate(range.start),
    to: parseDate(range.end),
  }));

  const { lastDate, singleDates, rangeStartDates, rangeEndDates, rangeMiddleDates } = handleFilledDates({
    ranges: booked,
  });

  const commonDatePickerProps = {
    defaultMonth: lastDate,
    modifiers: { rangeStartDates, rangeEndDates, rangeMiddleDates, singleDates },
    modifiersClassNames: {
      rangeStartDates: datePickerStyles.filledStart,
      rangeEndDates: datePickerStyles.filledEnd,
      rangeMiddleDates: datePickerStyles.filledMiddle,
      singleDates: datePickerStyles.filledSingle,
    },
  };

  return (
    <>
      <div
        ref={buttonContainerRef}
        className={cx(sharedStyles.datePickerButtonContainer, isNoData && sharedStyles.datePickerButtonContainerNoData)}
      >
        <button
          className={cx(sharedStyles.datePickerButton, isNoData && sharedStyles.datePickerButtonNoData)}
          disabled={readOnly}
          onClick={(event) => {
            handleOpenPopup(event, true);
          }}
        >
          {isNoData ? placeholder : formatDatesRange(range)}
          {!isNoData && showNightsCount && numberOfNights > 0 && (
            <div className={sharedStyles.numberOfNights}>{formatNumberOfNights(numberOfNights)}</div>
          )}
          {isNoData && showNightsCount && <div className={sharedStyles.numberOfNights}>3 Nights</div>}
        </button>
      </div>

      {isPopupOpen && (
        <Popover
          mountEl={mountEl}
          anchorEl={buttonContainerRef.current}
          onClose={handleClosePopup}
          position="left"
          shiftY={-8}
          shiftX={-20}
        >
          <div className={sharedStyles.popupContainer}>
            <div className={sharedStyles.switchItemTypeContainer}>
              <SwitchScheduleItemType scheduleItem={scheduleItem} />
            </div>

            <div className={sharedStyles.calendarContainer}>
              {noRange ? (
                <ResolvedDatePicker
                  mode="single"
                  selected={range.start}
                  onSelect={(dateStr) => onChange({ start: dateStr, end: dateStr })}
                  {...commonDatePickerProps}
                />
              ) : (
                <ResolvedDatePicker mode="range" selected={range} onSelect={onChange} {...commonDatePickerProps} />
              )}
            </div>
          </div>
        </Popover>
      )}
    </>
  );
}

export default DatePickerButton;
